import React, { useEffect, useMemo, useState } from 'react';
import { useProducts } from '../ProductContext';
import { apiJson } from '../apiClient';
import { mergedCategoriesForUser } from '../constants/businessStockCategories';
import { useAuth } from '../AuthContext';
import { isAutomotiveBusiness } from '../utils/businessType';
import { normalizeRole } from '../utils/userLocale';
import './ProductRequestPage.css';

export default function ProductRequestPage({ embedded = false }) {
  const { products: allProducts } = useProducts();
  const { user } = useAuth();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const automotiveMode = isAutomotiveBusiness(user);
  const categories = useMemo(
    () => mergedCategoriesForUser(user, allProducts || []),
    [allProducts, user]
  );
  const [products, setProducts] = useState([
    { productId: '', product_name: '', category: '', quantity: 1, estimatedUnitCostTzs: '', reason: '' },
  ]);
  const [notes, setNotes] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [neededByDate, setNeededByDate] = useState('');
  const [budgetTzs, setBudgetTzs] = useState('');
  const [priority, setPriority] = useState('normal');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const automotiveCatalog = useMemo(
    () =>
      (allProducts || [])
        .filter((p) => String(p.approvalStatus || 'approved') === 'approved')
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })),
    [allProducts]
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await apiJson('/suppliers');
        if (!mounted) return;
        setSuppliers(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (!mounted) return;
        setSuppliers([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const estimatedGrandTotal = useMemo(
    () =>
      products.reduce((sum, p) => {
        const qty = Math.max(1, parseInt(String(p.quantity), 10) || 1);
        const unit = Math.max(0, Number(p.estimatedUnitCostTzs) || 0);
        return sum + qty * unit;
      }, 0),
    [products]
  );

  const handleProductChange = (idx, field, value) => {
    const updated = [...products];
    updated[idx][field] = value;
    setProducts(updated);
  };

  const addProduct = () => {
    setProducts([...products, { productId: '', product_name: '', category: '', quantity: 1, estimatedUnitCostTzs: '', reason: '' }]);
  };

  const removeProduct = (idx) => {
    setProducts(products.filter((_, i) => i !== idx));
  };

  const composedNotes = () => {
    const lines = [];
    if (neededByDate) lines.push(`Needed by: ${neededByDate}`);
    if (budgetTzs && Number(budgetTzs) > 0) lines.push(`Budget (TZS): ${Number(budgetTzs).toLocaleString()}`);
    if (paymentTerms.trim()) lines.push(`Payment terms: ${paymentTerms.trim()}`);
    if (paymentDueDate) lines.push(`Payment due: ${paymentDueDate}`);
    lines.push(`Priority: ${priority}`);
    lines.push(`Estimated total (TZS): ${Math.round(estimatedGrandTotal).toLocaleString()}`);
    if (notes.trim()) lines.push(`Staff note: ${notes.trim()}`);
    return lines.join(' | ');
  };

  const saveSupplier = async () => {
    if (!isOwner) return;
    if (!supplierName.trim()) {
      setError('Write supplier name first, then save supplier.');
      return;
    }
    setAddingSupplier(true);
    setError('');
    try {
      const data = await apiJson('/owner/suppliers', {
        method: 'POST',
        body: JSON.stringify({
          name: supplierName.trim(),
          phone: supplierPhone.trim() || undefined,
        }),
      });
      const created = data.item;
      setSuppliers((prev) => [...prev, created].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
      setSupplierId(String(created.id));
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not save supplier.');
    } finally {
      setAddingSupplier(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);
    try {
      if (automotiveMode && products.some((p) => !(Number(p.estimatedUnitCostTzs) > 0))) {
        setError('Weka bei ya kununua kwa kila spare part kabla ya kutuma order.');
        setSubmitting(false);
        return;
      }
      const payload = {
        notes: composedNotes() || undefined,
        supplierId: supplierId ? Number(supplierId) : undefined,
        supplierName: supplierName.trim() || undefined,
        supplierPhone: supplierPhone.trim() || undefined,
        neededByDate: neededByDate || undefined,
        budgetTzs: budgetTzs ? Number(budgetTzs) : undefined,
        priority,
        paymentTerms: paymentTerms.trim() || undefined,
        paymentDueDate: paymentDueDate || undefined,
        products: products.map((p) => ({
          productId: p.productId ? Number(p.productId) : undefined,
          product_name: p.product_name,
          category: p.category || '',
          quantity: Math.max(1, parseInt(String(p.quantity), 10) || 1),
          estimatedUnitCostTzs: Math.max(0, Number(p.estimatedUnitCostTzs) || 0),
          reason: p.reason || undefined,
        })),
      };
      await apiJson('/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSuccess(true);
      setProducts([{ productId: '', product_name: '', category: '', quantity: 1, estimatedUnitCostTzs: '', reason: '' }]);
      setNotes('');
      setSupplierName('');
      setSupplierPhone('');
      setNeededByDate('');
      setBudgetTzs('');
      setPriority('normal');
      setPaymentTerms('');
      setPaymentDueDate('');
      setSupplierId('');
    } catch (err) {
      setError(err.body?.message || err.message || 'Submission failed');
    }
    setSubmitting(false);
  };

  return (
    <div
      className={`product-request-page${embedded ? ' product-request-page--embedded' : ' product-request-page--standalone page-bleed'}`}
    >
      <div className={embedded ? 'product-request-inner product-request-inner--embedded' : 'product-request-inner'}>
        <h2
          className={`product-request-title${embedded ? ' product-request-title--embedded' : ''}`}
          style={
            embedded
              ? { margin: '0 0 18px', fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', textAlign: 'left' }
              : { margin: '0 0 28px 0', fontWeight: 700, fontSize: 28, letterSpacing: '-1px', color: '#222', textAlign: 'center' }
          }
        >
          {embedded ? 'New product / other request' : 'Request new product / order'}
        </h2>
        <form
          className="product-request-form"
          style={embedded ? { background: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 } : { background: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 }}
          onSubmit={handleSubmit}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 10,
              marginBottom: 14,
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              background: '#f8fafc',
            }}
          >
            <select
              value={supplierId}
              onChange={(e) => {
                const id = e.target.value;
                setSupplierId(id);
                const chosen = suppliers.find((s) => String(s.id) === String(id));
                if (chosen) {
                  setSupplierName(chosen.name || '');
                  setSupplierPhone(chosen.phone || '');
                }
              }}
              className="product-request-input-plain"
              style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
            >
              <option value="">Select supplier (optional)</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.phone ? ` — ${s.phone}` : ''}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Supplier name (optional)"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              className="product-request-input-plain"
              style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
            />
            <input
              type="text"
              placeholder="Supplier phone"
              value={supplierPhone}
              onChange={(e) => setSupplierPhone(e.target.value)}
              className="product-request-input-plain"
              style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
            />
            {isOwner ? (
              <button
                type="button"
                onClick={() => void saveSupplier()}
                disabled={addingSupplier || !supplierName.trim()}
                style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a', fontSize: 13, fontWeight: 700 }}
              >
                {addingSupplier ? 'Saving...' : 'Save supplier'}
              </button>
            ) : null}
            <input
              type="date"
              value={neededByDate}
              onChange={(e) => setNeededByDate(e.target.value)}
              className="product-request-input-plain"
              style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
            />
            <input
              type="number"
              min="0"
              placeholder="Budget TZS"
              value={budgetTzs}
              onChange={(e) => setBudgetTzs(e.target.value)}
              className="product-request-input-plain"
              style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
            />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="product-request-input-plain"
              style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
            >
              <option value="low">Priority: Low</option>
              <option value="normal">Priority: Normal</option>
              <option value="high">Priority: High</option>
              <option value="urgent">Priority: Urgent</option>
            </select>
            <input
              type="text"
              placeholder="Payment terms (e.g. Net 30)"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="product-request-input-plain"
              style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
            />
            <input
              type="date"
              value={paymentDueDate}
              onChange={(e) => setPaymentDueDate(e.target.value)}
              className="product-request-input-plain"
              style={{ padding: '9px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
            />
          </div>
          {products.map((prod, idx) => (
            <div key={idx} className="product-request-form-group-plain" style={{ marginBottom: 18, borderBottom: 'none', paddingBottom: 0, position: 'relative', transition: 'all 0.3s', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {automotiveMode ? (
                <select
                  className="product-request-input-plain"
                  value={prod.productId || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    const chosen = automotiveCatalog.find((p) => String(p.id) === String(id));
                    setProducts((prev) =>
                      prev.map((item, itemIdx) =>
                        itemIdx === idx
                          ? {
                              ...item,
                              productId: id,
                              product_name: chosen ? chosen.name || '' : item.product_name,
                              category: chosen ? chosen.category || '' : item.category,
                              estimatedUnitCostTzs: chosen ? chosen.purchasingPrice || '' : item.estimatedUnitCostTzs,
                            }
                          : item
                      )
                    );
                  }}
                  style={{ flex: '2 1 220px', minWidth: 160, padding: '10px 12px', borderRadius: 3, border: '1px solid #bbb', fontSize: 16, background: '#fff' }}
                >
                  <option value="">Select registered spare part...</option>
                  {automotiveCatalog.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.partCode ? ` • ${p.partCode}` : ''}
                      {p.compatibleModels ? ` • ${p.compatibleModels}` : ''}
                    </option>
                  ))}
                </select>
              ) : null}
              <input
                type="text"
                className="product-request-input-plain"
                placeholder={automotiveMode ? 'Part name / custom spare part' : 'Product Name'}
                value={prod.product_name}
                onChange={e => handleProductChange(idx, 'product_name', e.target.value)}
                required
                disabled={automotiveMode && Boolean(prod.productId)}
                style={{ flex: '2 1 180px', minWidth: 120, padding: '10px 12px', borderRadius: 3, border: '1px solid #bbb', fontSize: 16, background: '#fff' }}
              />
              <select
                className="product-request-input-plain"
                value={prod.category}
                onChange={e => handleProductChange(idx, 'category', e.target.value)}
                disabled={automotiveMode && Boolean(prod.productId)}
                style={{ flex: '1 1 120px', minWidth: 90, padding: '10px 12px', borderRadius: 3, border: '1px solid #bbb', fontSize: 16, background: '#fff' }}
              >
                <option value="">Select category…</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min="1"
                className="product-request-input-plain"
                placeholder="Qty"
                value={prod.quantity}
                onChange={e => handleProductChange(idx, 'quantity', e.target.value)}
                required
                style={{ flex: '0.7 1 60px', minWidth: 60, padding: '10px 12px', borderRadius: 3, border: '1px solid #bbb', fontSize: 16, background: '#fff' }}
              />
              <input
                type="number"
                min="0"
                className="product-request-input-plain"
                placeholder={automotiveMode ? 'Buy price / unit (TZS)' : 'Unit cost (TZS)'}
                value={prod.estimatedUnitCostTzs}
                onChange={e => handleProductChange(idx, 'estimatedUnitCostTzs', e.target.value)}
                required={automotiveMode}
                style={{ flex: '1 1 130px', minWidth: 110, padding: '10px 12px', borderRadius: 3, border: '1px solid #bbb', fontSize: 16, background: '#fff' }}
              />
              <input
                type="text"
                className="product-request-input-plain"
                placeholder="Reason (optional)"
                value={prod.reason}
                onChange={e => handleProductChange(idx, 'reason', e.target.value)}
                style={{ flex: '2.5 1 220px', minWidth: 140, padding: '10px 12px', borderRadius: 3, border: '1px solid #bbb', fontSize: 16, background: '#fff' }}
              />
              {products.length > 1 && (
                <button type="button" className="product-request-remove-btn-plain" onClick={() => removeProduct(idx)} title="Remove" style={{ color: '#d32f2f', background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', marginLeft: 4 }}>×</button>
              )}
            </div>
          ))}
          <button type="button" className="product-request-add-btn-plain" onClick={addProduct} style={{ width: 'auto', margin: '8px 0 12px 0', background: '#1976d2', color: '#fff', fontWeight: 600, borderRadius: 3, padding: '8px 18px', fontSize: 16, border: 'none', cursor: 'pointer' }}>+ Add Another Product</button>
          <div style={{ marginBottom: 10, fontWeight: 700, color: '#0f172a' }}>
            Estimated total: TZS {Math.round(estimatedGrandTotal).toLocaleString()}
          </div>
          <div className="product-request-notes-group" style={{ marginTop: 12 }}>
            <textarea
              className="product-request-notes-plain"
              placeholder="Notes for boss (optional)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ width: '100%', minHeight: 60, padding: '10px 12px', borderRadius: 3, border: '1px solid #bbb', marginBottom: 16, fontSize: 16, background: '#fff' }}
            />
          </div>
          <button type="submit" className="product-request-submit-btn-plain" disabled={submitting} style={{ width: '100%', background: '#2e7d32', color: '#fff', fontWeight: 700, borderRadius: 3, padding: '12px 0', fontSize: 18, border: 'none', cursor: 'pointer' }}>
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
          {error && (
            <div className="product-request-inline-msg product-request-inline-msg--error" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="product-request-inline-msg product-request-inline-msg--success" role="status">
              Request submitted. Check the order list for status.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
